import React from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useParams,
} from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import "./i18n";
import { Layout } from "./components/layout/Layout";
import { DashboardPage } from "./components/pages/dashboard/DashboardPage";
import {
  AnalyticsPage,
  FrontDeskAnalyticsRouteGuard,
} from "./components/pages/analytics/AnalyticsPage";
import { AnalyticsServicesListPage } from "./components/pages/analytics/front-desk/AnalyticsServicesListPage";
import { AnalyticsServiceDetailPage } from "./components/pages/analytics/front-desk/AnalyticsServiceDetailPage";
import { ReservationsPage } from "./components/pages/reservations/ReservationsPage";
import { AddReservationPage } from "./components/pages/reservations/AddReservationPage";
import { EditReservationPage } from "./components/pages/reservations/EditReservationPage";
import { RoomsPage } from "./components/pages/reservations/RoomsPage";
import { GuestsPage } from "./components/pages/reservations/guests/GuestsPage";
import { GuestPage } from "./components/pages/reservations/guest_page/GuestPage";
import { EditRoomPage } from "./components/pages/reservations/EditRoomPage";
import { RoomsCategoriesPage } from "./components/pages/reservations/RoomCategoriesPage";
import { AddCategoryPage } from "./components/pages/reservations/AddCategoryPage";
import { RoomProvider } from "./contexts/RoomContext";
import LoginPage from "@/components/pages/login/LoginPage";
import AuthCallbackPage from "@/components/pages/login/AuthCallbackPage";
import {
  AuthGuard,
  CapabilityGuard,
  CoreFeatureGuard,
  CorePlatformGuard,
  PlatformPermissionGuard,
  RoleBasedRedirect,
  RoleGuard,
  SuperAdminGuard,
  TenantPermissionGuard,
} from "@/components/auth";
import { TenantEditPage } from "@/components/pages/tenants/TenantEditPage";
import { TenantsPage } from "@/components/pages/tenants/TenantsPage";
import { SubscriptionsPage } from "@/components/pages/subscriptions/SubscriptionsPage";
import { RequestsPage } from "./components/pages/requests/RequestsPage";
import { RequestDetailsPage } from "./components/pages/requests/RequestDetailsPage";
import { HotelsPage } from "./components/pages/hotels/HotelsPage";
import { Toaster } from "./components/ui/toast";
import { ArrivalsPage } from "@/components/pages/settlement/pages/ArrivalsPage";
import { DeparturesPage } from "@/components/pages/settlement/pages/DeparturesPage";
import { InHousePage } from "@/components/pages/settlement/pages/InHousePage";
import AddRoomPage from "./components/pages/reservations/AddRoomPage";
import { RoomCategoriesProvider } from "./contexts/RoomCategoriesContext";
import EditCategoryPage from "./components/pages/reservations/EditRoomCategoryPage";
import { UsersPage } from "@/components/pages/users/UsersPage";
import { UserDetailPage } from "@/components/pages/users/UserDetailPage";
import { UserCreatePage } from "@/components/pages/users/UserCreatePage";
import { GroupCreatePage } from "@/components/pages/users/GroupCreatePage";
import { GroupEditPage } from "@/components/pages/users/GroupEditPage";
import { SuperAdminUserEditPage } from "@/components/pages/users/SuperAdminUserEditPage";
import { ServicesPage } from "./components/pages/services/ServicesPage";
import { ServiceProvider } from "./contexts/ServiceContext";
import AddServicePage from "./components/pages/services/AddServicePage";
import ServicePage from "./components/pages/services/ServicePage";
import ActivityDetailPage from "./components/pages/services/ActivityDetailPage";
import { ServiceSearchRedirectPage } from "./components/pages/services/ServiceSearchRedirectPage";
import { NavigationProvider } from "./components/navigation/NavigationProvider";
import { cacheService } from "./utils/cacheService";
import { CheckinSettingsPage } from "./components/pages/checkin-settings/CheckinSettingsPage";
import { CheckoutSettingsPage } from "./components/pages/checkout-settings/CheckoutSettingsPage";
import ChatsPage from "@/components/pages/chats/ChatsPage";
import { AiSettingsPage } from "@/components/pages/chats/AiSettingsPage";
import InternalChatsPage from "@/components/pages/chats/InternalChatsPage";
import AmenitiesPage from "@/components/pages/amenities/AmenitiesPage";
import CreateAmenityPage from "@/components/pages/amenities/CreateAmenityPage";
import EditAmenityPage from "@/components/pages/amenities/EditAmenityPage";
import ViewAmenityPage from "@/components/pages/amenities/ViewAmenityPage";
import ServiceCategoriesPage from "./components/pages/services/CategoriesPage";

import { StaffPage } from "./components/pages/staff/StaffPage";
import { AddStaffPage } from "./components/pages/staff/AddStaffPage";
import { EditStaffPage } from "./components/pages/staff/EditStaffPage";
import { StaffRolesPage } from "./components/pages/staff/StaffRolesPage";
import { AddTeamPage } from "./components/pages/staff/AddTeamPage";
import { EditTeamPage } from "./components/pages/staff/EditTeamPage";
import { TeamsPage } from "./components/pages/staff/TeamsPage";
import { MobileSettingsPage } from "./components/pages/mobile-settings/MobileSettingsPage";
import { DashboardSettingsPage } from "./components/pages/mobile-settings/DashboardSettingsPage";
import { FAQsPage } from "./components/pages/faqs/FAQsPage";
import { CannedResponsesPage } from "./components/pages/canned-responses/CannedResponsesPage";
import { FeedbacksPage } from "./components/pages/feedbacks/FeedbacksPage";
import { FeedbackSettingsPage } from "./components/pages/feedback-settings/FeedbackSettingsPage";
import { BillingSettingsPage } from "./components/pages/billing-settings/BillingSettingsPage";
import { BillingSettingsRefreshPage } from "./components/pages/billing-settings/BillingSettingsRefreshPage";
import { CommunicationIntegrationsPage } from "./components/pages/communication-integrations/CommunicationIntegrationsPage";
import { CommunicationChannelsPage } from "./components/pages/communication-channels/CommunicationChannelsPage";
import { CommunicationDeliveriesPage } from "./components/pages/communication-deliveries/CommunicationDeliveriesPage";
import { PartnerIntegrationSettingsPage } from "./components/pages/partner-integration-settings/PartnerIntegrationSettingsPage";
import { GuesteriaPartnersPage } from "./components/pages/guesteria-partners/GuesteriaPartnersPage";
import { GuesteriaPartnerDetailPage } from "./components/pages/guesteria-partners/GuesteriaPartnerDetailPage";
import { PmsPage } from "./components/pages/pms/PmsPage";
import { NotFoundPage } from "./components/pages/not-found/NotFoundPage";
import { BillingTransactionProvider } from "./contexts/BillingTransactionsContext";
import { BillingTransactionsPage } from "./components/pages/admin-billing-transactions/BillingTransactionsPage";
import { BillingSubscriptionsPage } from "@/components/pages/billing-subscriptions";
import { SubscriptionPaymentsPage } from "./components/pages/subscription-payments/SubscriptionPaymentsPage";
import { GuesteriaPartnersRevenuePage } from "./components/pages/admin-billing-transactions/GuesteriaPartnersRevenuePage";
import { GuesteriaPartnersRevenueDetailPage } from "./components/pages/admin-billing-transactions/GuesteriaPartnersRevenueDetailPage";
import { NotificationsToastProvider } from "./contexts/NotificationsCtx";
import { NotificationsSettingsPage } from "./components/pages/notifications-settings";
import { MessagingSettingsPage } from "./components/pages/messaging-settings/MessagingSettingsPage";
import { RequestsSettingsPage } from "./components/pages/requests-settings/RequestsSettingsPage";
import { DesignSystemPage } from "./components/pages/design-system/DesignSystemPage";
import { ChatProvider } from "./components/chat/ChatProvider";
import { CustomPagesListPage } from "./components/pages/custom-pages/CustomPagesListPage";
import { CreateCustomPagePage } from "./components/pages/custom-pages/CreateCustomPagePage";
import { EditCustomPagePage } from "./components/pages/custom-pages/EditCustomPagePage";
import { PrivacyPolicyPage } from "@/components/pages/privacy-policy/PrivacyPolicyPage";
import { TermsOfUsePage } from "@/components/pages/terms-of-use/TermsOfUsePage";
import { AccountPage } from "./components/pages/account/AccountPage";
import { MonitoringSettingsPage } from "./components/pages/monitoring/MonitoringSettingsPage";
import { MonitoringDashboardPage } from "./components/pages/monitoring/MonitoringDashboardPage";
import { UsersMonitoringPage } from "./components/pages/monitoring/UsersMonitoringPage";
import { PlatformAnalyticsDashboardPage } from "@/components/pages/admin-analytics/PlatformAnalyticsDashboardPage";
import { PartnersPage } from "./components/pages/admin-partners/PartnersPage";
import { DestinationsPage } from "./components/pages/admin-partners/DestinationsPage";
import { DestinationDetailPage } from "./components/pages/admin-partners/DestinationDetailPage";
import { PartnerRevenueSummaryPage } from "./components/pages/admin-partners/PartnerRevenueSummaryPage";
import { ScheduleManagementPage } from "./components/pages/staff-schedule/ScheduleManagementPage";
import { ModulesPage } from "./components/pages/modules/ModulesPage";
import { AppLinksSettingsPage } from "@/components/pages/app-links-settings/AppLinksSettingsPage";
import { SettingsRoutePage } from "@/components/pages/settings/SettingsRoutePage";
import { GeneralInfoPage } from "@/components/pages/general-info/GeneralInfoPage";
import { RegionSettingsPage } from "./components/pages/admin-regions/RegionSettingsPage";
import { DeletionsPage } from "@/components/pages/data-deletion/DeletionsPage";
import { ExportsPage } from "@/components/pages/data-export/ExportsPage";
import { StorageListPage } from "./components/pages/admin-storage/StorageListPage";
import { StorageRegionDetailPage } from "./components/pages/admin-storage/StorageRegionDetailPage";
import { StorageTenantDetailPage } from "./components/pages/admin-storage/StorageTenantDetailPage";
import { Role } from "@/types/user";
import {
  COMMUNICATION_AUDIT_ROLES,
  COMMUNICATION_INTEGRATION_ROLES,
  isPlatformAdminRole,
  isRapidPlan,
  PlatformRole,
  PMS_INTEGRATION_ROLES,
} from "@/utils/accessControl";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";

const LIVE_DATA_REFETCH_INTERVAL_MS = 30_000;
const LIVE_DATA_QUERY_ROOTS = new Set<unknown>([
  "booking-checkin",
  "channels",
  "communication-deliveries",
  "dashboard",
  "feedbacks",
  "guests",
  "notifications",
  "request-comments",
  "request-feedback",
  "requests",
  "reservations",
  "rooms",
  "settlement",
  "staff",
  "tasks",
  "userHotel",
  "whatsapp",
]);

// Create a global query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      retry: 1,
      refetchOnMount: "always",
      refetchOnReconnect: "always",
      refetchInterval: (query) =>
        LIVE_DATA_QUERY_ROOTS.has(query.queryKey[0])
          ? LIVE_DATA_REFETCH_INTERVAL_MS
          : false,
      refetchIntervalInBackground: false,
      refetchOnWindowFocus: "always",
    },
  },
});

// Initialize cache service with query client
cacheService.setQueryClient(queryClient);

const PLATFORM_TENANT_ROLES = [
  PlatformRole.SUPER_ADMIN,
  PlatformRole.GUESTERIA_ADMIN,
] as const;

const DATA_DELETION_ROLES = [PlatformRole.SUPER_ADMIN] as const;
const DATA_EXPORT_ROLES = [PlatformRole.SUPER_ADMIN] as const;

const TENANT_DETAIL_ROLES = [
  PlatformRole.SUPER_ADMIN,
  PlatformRole.GUESTERIA_ADMIN,
  Role.TENANT_ADMIN,
] as const;

const USERS_PAGE_ROLES = [
  PlatformRole.SUPER_ADMIN,
  PlatformRole.GUESTERIA_ADMIN,
  Role.TENANT_ADMIN,
] as const;

const TENANT_ADMIN_ONLY_ROLES = [Role.TENANT_ADMIN] as const;
const HOTEL_SCOPED_ROLES = [
  Role.HOTEL_ADMIN,
  Role.FRONT_DESK,
  Role.STAFF,
] as const;
const USER_CREATE_ROLES = [
  PlatformRole.SUPER_ADMIN,
  PlatformRole.GUESTERIA_ADMIN,
  Role.TENANT_ADMIN,
] as const;
const ACCESS_GROUP_CREATE_ROLES = [
  PlatformRole.SUPER_ADMIN,
  Role.TENANT_ADMIN,
] as const;
// Detail/preview screens open in read-only mode for "View" roles, so they must
// be reachable with either the read or the administrate permission.
const USERS_VIEW_OR_ADMIN_PERMISSIONS = [
  "users:read",
  "users:administrate",
] as const;

const LegacyCustomPageRedirect = (): React.JSX.Element => {
  const { id } = useParams();

  return <Navigate replace to={id ? `/custom-pages/${id}` : "/custom-pages"} />;
};

const NonRapidTenantPlanGuard = ({
  children,
  fallbackPath = "/hotels",
}: {
  children: React.ReactNode;
  fallbackPath?: string;
}): React.JSX.Element | null => {
  const { userProfile, isLoading } = useAuthenticatedUser();

  if (isLoading) {
    return null;
  }

  if (isRapidPlan(userProfile)) {
    return <Navigate replace to={fallbackPath} />;
  }

  return <>{children}</>;
};

const UserCreatePermissionGuard = ({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element => {
  const { userProfile } = useAuthenticatedUser();

  if (isPlatformAdminRole(userProfile)) {
    return (
      <PlatformPermissionGuard
        permission="platform:users:administrate"
        fallbackPath="/users"
      >
        {children}
      </PlatformPermissionGuard>
    );
  }

  return (
    <TenantPermissionGuard
      permission="users:administrate"
      fallbackPath="/users"
    >
      {children}
    </TenantPermissionGuard>
  );
};

const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider delayDuration={300}>
        <NotificationsToastProvider>
          <RoomProvider>
            <RoomCategoriesProvider>
              <ServiceProvider>
                <BillingTransactionProvider>
                  <BrowserRouter>
                    <NavigationProvider>
                      <Routes>
                        <Route path="/login" element={<LoginPage />} />
                        <Route
                          path="/auth/callback"
                          element={<AuthCallbackPage />}
                        />
                        <Route
                          path="/"
                          element={
                            <AuthGuard>
                              <ChatProvider>
                                <Layout />
                              </ChatProvider>
                            </AuthGuard>
                          }
                        >
                          <Route path="dashboard" element={<DashboardPage />} />
                          <Route path="analytics" element={<AnalyticsPage />} />
                          <Route
                            path="analytics/hotels/:hotelId"
                            element={<AnalyticsPage />}
                          />
                          <Route
                            path="analytics/services/:serviceId"
                            element={
                              <FrontDeskAnalyticsRouteGuard>
                                <AnalyticsServiceDetailPage />
                              </FrontDeskAnalyticsRouteGuard>
                            }
                          />
                          <Route
                            path="analytics/services"
                            element={
                              <FrontDeskAnalyticsRouteGuard>
                                <AnalyticsServicesListPage />
                              </FrontDeskAnalyticsRouteGuard>
                            }
                          />
                          <Route
                            path="analytics-fd"
                            element={
                              <FrontDeskAnalyticsRouteGuard>
                                <AnalyticsPage />
                              </FrontDeskAnalyticsRouteGuard>
                            }
                          />
                          <Route
                            path="analytics-fd/services/:serviceId"
                            element={
                              <FrontDeskAnalyticsRouteGuard>
                                <AnalyticsServiceDetailPage />
                              </FrontDeskAnalyticsRouteGuard>
                            }
                          />
                          <Route
                            path="analytics-fd/services"
                            element={
                              <FrontDeskAnalyticsRouteGuard>
                                <AnalyticsServicesListPage />
                              </FrontDeskAnalyticsRouteGuard>
                            }
                          />
                          <Route
                            path="arrivals"
                            element={
                              <CoreFeatureGuard feature="arrivals">
                                <ArrivalsPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="arrivals/:id"
                            element={
                              <CoreFeatureGuard feature="arrivals">
                                <EditReservationPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="departures"
                            element={
                              <CoreFeatureGuard feature="departures">
                                <DeparturesPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="departures/:id"
                            element={
                              <CoreFeatureGuard feature="departures">
                                <EditReservationPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="in-house"
                            element={
                              <CoreFeatureGuard feature="in_house">
                                <InHousePage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="in-house/:id"
                            element={
                              <CoreFeatureGuard feature="in_house">
                                <EditReservationPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="partners"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:partners:read">
                                  <PartnersPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="partners/destinations"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:partners:read">
                                  <DestinationsPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="partners/destinations/:destinationId"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:partners:read">
                                  <DestinationDetailPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="partners/revenue"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:partners:read">
                                  <PartnerRevenueSummaryPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="tenants"
                            element={
                              <RoleGuard roles={PLATFORM_TENANT_ROLES}>
                                <PlatformPermissionGuard permission="platform:tenants:read">
                                  <TenantsPage />
                                </PlatformPermissionGuard>
                              </RoleGuard>
                            }
                          />
                          <Route
                            path="tenants/:tenantId/edit"
                            element={
                              <RoleGuard roles={TENANT_DETAIL_ROLES}>
                                <TenantEditPage />
                              </RoleGuard>
                            }
                          />
                          <Route
                            path="subscriptions"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:subscriptions:read">
                                  <SubscriptionsPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="users"
                            element={
                              <RoleGuard roles={USERS_PAGE_ROLES}>
                                <UsersPage />
                              </RoleGuard>
                            }
                          />
                          <Route
                            path="staff"
                            element={
                              <CapabilityGuard capability="canManageStaffOperations">
                                <StaffPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="staff/add"
                            element={
                              <CapabilityGuard capability="canManageStaffOperations">
                                <AddStaffPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="staff/:id"
                            element={
                              <CapabilityGuard capability="canManageStaffOperations">
                                <EditStaffPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="staff/teams"
                            element={
                              <CapabilityGuard capability="canManageStaffOperations">
                                <TeamsPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="staff/teams/add"
                            element={
                              <CapabilityGuard capability="canManageStaffOperations">
                                <AddTeamPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="staff/teams/:id"
                            element={
                              <CapabilityGuard capability="canManageStaffOperations">
                                <EditTeamPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="staff/roles"
                            element={
                              <RoleGuard
                                roles={[
                                  Role.TENANT_ADMIN,
                                  Role.HOTEL_ADMIN,
                                  Role.FRONT_DESK,
                                ]}
                                fallbackPath="/staff"
                              >
                                <CapabilityGuard capability="canManageStaffOperations">
                                  <StaffRolesPage />
                                </CapabilityGuard>
                              </RoleGuard>
                            }
                          />
                          <Route
                            path="staff-schedule"
                            element={
                              <CapabilityGuard capability="canManageStaffOperations">
                                <ScheduleManagementPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="users/:id"
                            element={
                              <RoleGuard roles={TENANT_ADMIN_ONLY_ROLES}>
                                <TenantPermissionGuard
                                  permission={USERS_VIEW_OR_ADMIN_PERMISSIONS}
                                  fallbackPath="/users"
                                >
                                  <UserDetailPage />
                                </TenantPermissionGuard>
                              </RoleGuard>
                            }
                          />
                          <Route
                            path="users/new"
                            element={
                              <RoleGuard roles={USER_CREATE_ROLES}>
                                <UserCreatePermissionGuard>
                                  <UserCreatePage />
                                </UserCreatePermissionGuard>
                              </RoleGuard>
                            }
                          />
                          <Route
                            path="users/guesteria-admins/:id/edit"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard
                                  permission="platform:users:administrate"
                                  fallbackPath="/users"
                                >
                                  <SuperAdminUserEditPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="users/groups/new"
                            element={
                              <RoleGuard roles={ACCESS_GROUP_CREATE_ROLES}>
                                <TenantPermissionGuard
                                  permission="users:administrate"
                                  fallbackPath="/users"
                                >
                                  <GroupCreatePage />
                                </TenantPermissionGuard>
                              </RoleGuard>
                            }
                          />
                          <Route
                            path="users/groups/:id/edit"
                            element={
                              <RoleGuard roles={ACCESS_GROUP_CREATE_ROLES}>
                                <TenantPermissionGuard
                                  permission={USERS_VIEW_OR_ADMIN_PERMISSIONS}
                                  fallbackPath="/users"
                                >
                                  <GroupEditPage />
                                </TenantPermissionGuard>
                              </RoleGuard>
                            }
                          />
                          <Route
                            path="requests"
                            element={
                              <CapabilityGuard capability="canUseRequestsSystem">
                                <RequestsPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="requests/:id"
                            element={
                              <CapabilityGuard capability="canUseRequestsSystem">
                                <RequestDetailsPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route path="hotels" element={<HotelsPage />} />
                          <Route
                            path="reservations"
                            element={
                              <CoreFeatureGuard feature="reservations">
                                <ReservationsPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="reservations/rooms"
                            element={
                              <CoreFeatureGuard feature="rooms">
                                <TenantPermissionGuard
                                  permission={[
                                    "rooms:read",
                                    "rooms:administrate",
                                  ]}
                                >
                                  <RoomsPage />
                                </TenantPermissionGuard>
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="reservations/guests"
                            element={
                              <CoreFeatureGuard feature="guests">
                                <GuestsPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="reservations/guests/:id"
                            element={
                              <CoreFeatureGuard feature="guests">
                                <GuestPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route index element={<RoleBasedRedirect />} />
                          <Route
                            path="reservations/add"
                            element={
                              <CoreFeatureGuard feature="reservations">
                                <AddReservationPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="reservations/:id"
                            element={
                              <CoreFeatureGuard feature="reservations">
                                <EditReservationPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="reservations/rooms/add"
                            element={
                              <CoreFeatureGuard feature="rooms">
                                <TenantPermissionGuard
                                  permission="rooms:administrate"
                                  fallbackPath="/reservations/rooms"
                                >
                                  <AddRoomPage />
                                </TenantPermissionGuard>
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="reservations/rooms/:id"
                            element={
                              <CoreFeatureGuard feature="rooms">
                                <TenantPermissionGuard
                                  permission={[
                                    "rooms:read",
                                    "rooms:administrate",
                                  ]}
                                  fallbackPath="/reservations/rooms"
                                >
                                  <EditRoomPage />
                                </TenantPermissionGuard>
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="reservations/rooms/categories"
                            element={
                              <CoreFeatureGuard feature="rooms">
                                <TenantPermissionGuard
                                  permission={[
                                    "rooms:read",
                                    "rooms:administrate",
                                  ]}
                                  fallbackPath="/reservations/rooms"
                                >
                                  <RoomsCategoriesPage />
                                </TenantPermissionGuard>
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="reservations/rooms/categories/add"
                            element={
                              <CoreFeatureGuard feature="rooms">
                                <TenantPermissionGuard
                                  permission="rooms:administrate"
                                  fallbackPath="/reservations/rooms/categories"
                                >
                                  <AddCategoryPage />
                                </TenantPermissionGuard>
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="reservations/rooms/categories/:id"
                            element={
                              <CoreFeatureGuard feature="rooms">
                                <TenantPermissionGuard
                                  permission={[
                                    "rooms:read",
                                    "rooms:administrate",
                                  ]}
                                  fallbackPath="/reservations/rooms/categories"
                                >
                                  <EditCategoryPage />
                                </TenantPermissionGuard>
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="amenities"
                            element={
                              <CoreFeatureGuard feature="amenities">
                                <AmenitiesPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="amenities/create"
                            element={
                              <CoreFeatureGuard feature="amenities">
                                <CreateAmenityPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="amenities/:id/view"
                            element={
                              <CoreFeatureGuard feature="amenities">
                                <ViewAmenityPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="amenities/:id"
                            element={
                              <CoreFeatureGuard feature="amenities">
                                <EditAmenityPage />
                              </CoreFeatureGuard>
                            }
                          />

                          <Route
                            path="services"
                            element={
                              <CapabilityGuard capability="canManageServices">
                                <ServicesPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="services/add"
                            element={
                              <CapabilityGuard capability="canManageServices">
                                <AddServicePage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="services/:id"
                            element={
                              <CapabilityGuard capability="canManageServices">
                                <ServicePage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="services/search/:id"
                            element={
                              <CapabilityGuard capability="canManageServices">
                                <ServiceSearchRedirectPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="services/activity/:id"
                            element={
                              <CapabilityGuard capability="canManageServices">
                                <ActivityDetailPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="services/categories"
                            element={
                              <CapabilityGuard capability="canManageServices">
                                <ServiceCategoriesPage />
                              </CapabilityGuard>
                            }
                          />

                          <Route
                            path="checkin-settings"
                            element={
                              <CapabilityGuard capability="canUseCheckinCheckout">
                                <CheckinSettingsPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="checkout-settings"
                            element={
                              <CapabilityGuard capability="canUseCheckinCheckout">
                                <CheckoutSettingsPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="general-info"
                            element={
                              <RoleGuard roles={HOTEL_SCOPED_ROLES}>
                                <CoreFeatureGuard feature="hotel_brand_settings">
                                  <GeneralInfoPage />
                                </CoreFeatureGuard>
                              </RoleGuard>
                            }
                          />
                          <Route
                            path="mobile-settings"
                            element={
                              <CoreFeatureGuard feature="hotel_brand_settings">
                                <MobileSettingsPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="custom-pages"
                            element={
                              <CapabilityGuard capability="canUseCustomPages">
                                <CustomPagesListPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="custom-pages/new"
                            element={
                              <CapabilityGuard capability="canUseCustomPages">
                                <CreateCustomPagePage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="custom-pages/:id"
                            element={
                              <CapabilityGuard capability="canUseCustomPages">
                                <EditCustomPagePage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="mobile-settings/custom-pages"
                            element={<Navigate replace to="/custom-pages" />}
                          />
                          <Route
                            path="mobile-settings/custom-pages/new"
                            element={
                              <Navigate replace to="/custom-pages/new" />
                            }
                          />
                          <Route
                            path="mobile-settings/custom-pages/:id"
                            element={<LegacyCustomPageRedirect />}
                          />
                          <Route
                            path="mobile-settings/dashboard"
                            element={
                              <CoreFeatureGuard feature="hotel_brand_settings">
                                <DashboardSettingsPage />
                              </CoreFeatureGuard>
                            }
                          />
                          <Route
                            path="faqs"
                            element={
                              <CapabilityGuard capability="canManageFaqs">
                                <FAQsPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="canned-responses"
                            element={
                              <CapabilityGuard capability="canUseGuestCommunication">
                                <CannedResponsesPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path={"ai"}
                            element={
                              <CapabilityGuard capability="canUseAiAssistant">
                                <AiSettingsPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path={"chats"}
                            element={
                              <CapabilityGuard capability="canUseGuestCommunication">
                                <ChatsPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path={"internal-chats"}
                            element={
                              <CapabilityGuard capability="canUseInternalChat">
                                <InternalChatsPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path={"feedbacks"}
                            element={
                              <CapabilityGuard capability="canUseFeedbacks">
                                <FeedbacksPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path={"feedback-settings"}
                            element={
                              <CapabilityGuard capability="canUseFeedbacks">
                                <FeedbackSettingsPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path="requests-settings"
                            element={
                              <CapabilityGuard capability="canUseRequestsSystem">
                                <RequestsSettingsPage />
                              </CapabilityGuard>
                            }
                          />

                          <Route
                            path={"billing-settings"}
                            element={
                              <RoleGuard roles={[Role.HOTEL_ADMIN]}>
                                <CapabilityGuard capability="canViewBilling">
                                  <BillingSettingsPage />
                                </CapabilityGuard>
                              </RoleGuard>
                            }
                          />
                          <Route
                            path={"billing-settings/refresh"}
                            element={
                              <RoleGuard roles={[Role.HOTEL_ADMIN]}>
                                <CapabilityGuard capability="canManageBilling">
                                  <BillingSettingsRefreshPage />
                                </CapabilityGuard>
                              </RoleGuard>
                            }
                          />
                          <Route
                            path={"billing-settings/transactions"}
                            element={
                              <RoleGuard roles={[Role.HOTEL_ADMIN]}>
                                <CapabilityGuard capability="canViewBilling">
                                  <SubscriptionPaymentsPage />
                                </CapabilityGuard>
                              </RoleGuard>
                            }
                          />

                          <Route
                            path={"partner-integration-settings"}
                            element={
                              <CapabilityGuard capability="canUseGuesteriaPartners">
                                <PartnerIntegrationSettingsPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path={"partners-overview"}
                            element={<Navigate replace to="/hotels" />}
                          />
                          <Route
                            path={"guesteria-partners"}
                            element={
                              <CapabilityGuard capability="canUseGuesteriaPartners">
                                <GuesteriaPartnersPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path={"guesteria-partners/:partnerId"}
                            element={
                              <CapabilityGuard capability="canUseGuesteriaPartners">
                                <GuesteriaPartnerDetailPage />
                              </CapabilityGuard>
                            }
                          />

                          <Route
                            path={"pms-integration"}
                            element={
                              <RoleGuard roles={PMS_INTEGRATION_ROLES}>
                                <CoreFeatureGuard feature="pms">
                                  <PmsPage />
                                </CoreFeatureGuard>
                              </RoleGuard>
                            }
                          />

                          <Route
                            path={"settings"}
                            element={<SettingsRoutePage />}
                          />

                          <Route
                            path={"billing-subscriptions"}
                            element={
                              <CapabilityGuard capability="canViewBilling">
                                <BillingSubscriptionsPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path={"billing-transactions"}
                            element={
                              <CapabilityGuard capability="canViewBilling">
                                <BillingTransactionsPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path={"subscription-payments"}
                            element={
                              <CapabilityGuard capability="canViewBilling">
                                <SubscriptionPaymentsPage />
                              </CapabilityGuard>
                            }
                          />
                          <Route
                            path={"guesteria-partners-revenue"}
                            element={
                              <RoleGuard roles={TENANT_ADMIN_ONLY_ROLES}>
                                <NonRapidTenantPlanGuard>
                                  <CapabilityGuard capability="canViewBilling">
                                    <GuesteriaPartnersRevenuePage />
                                  </CapabilityGuard>
                                </NonRapidTenantPlanGuard>
                              </RoleGuard>
                            }
                          />
                          <Route
                            path={"guesteria-partners-revenue/:hotelId"}
                            element={
                              <RoleGuard roles={TENANT_ADMIN_ONLY_ROLES}>
                                <NonRapidTenantPlanGuard>
                                  <CapabilityGuard capability="canViewBilling">
                                    <GuesteriaPartnersRevenueDetailPage />
                                  </CapabilityGuard>
                                </NonRapidTenantPlanGuard>
                              </RoleGuard>
                            }
                          />

                          <Route
                            path={"notifications-settings"}
                            element={
                              <TenantPermissionGuard
                                permission={[
                                  "settings:read",
                                  "settings:administrate",
                                ]}
                              >
                                <CoreFeatureGuard feature="notifications">
                                  <NotificationsSettingsPage />
                                </CoreFeatureGuard>
                              </TenantPermissionGuard>
                            }
                          />
                          <Route
                            path="messaging-settings"
                            element={
                              <CorePlatformGuard>
                                <CapabilityGuard capability="canUseGuestCommunication">
                                  <MessagingSettingsPage />
                                </CapabilityGuard>
                              </CorePlatformGuard>
                            }
                          />

                          <Route
                            path="communication-integrations"
                            element={
                              <RoleGuard
                                roles={COMMUNICATION_INTEGRATION_ROLES}
                              >
                                <CapabilityGuard capability="canUseGuestCommunication">
                                  <CommunicationIntegrationsPage />
                                </CapabilityGuard>
                              </RoleGuard>
                            }
                          />

                          <Route
                            path="communication-channels"
                            element={
                              <RoleGuard
                                roles={COMMUNICATION_INTEGRATION_ROLES}
                              >
                                <CapabilityGuard capability="canUseGuestCommunication">
                                  <CommunicationChannelsPage />
                                </CapabilityGuard>
                              </RoleGuard>
                            }
                          />

                          <Route
                            path="communication-deliveries"
                            element={
                              <RoleGuard roles={COMMUNICATION_AUDIT_ROLES}>
                                <CapabilityGuard capability="canUseGuestCommunication">
                                  <CommunicationDeliveriesPage />
                                </CapabilityGuard>
                              </RoleGuard>
                            }
                          />

                          <Route
                            path={"design-system"}
                            element={<DesignSystemPage />}
                          />
                          <Route
                            path={"privacy-policy"}
                            element={<PrivacyPolicyPage mode="view" />}
                          />
                          <Route
                            path={"terms-of-use"}
                            element={<TermsOfUsePage mode="view" />}
                          />
                          <Route
                            path={"edit-privacy-policy"}
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:settings:read">
                                  <PrivacyPolicyPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path={"edit-terms-of-use"}
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:settings:read">
                                  <TermsOfUsePage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />

                          <Route path="account" element={<AccountPage />} />
                          <Route
                            path="admin/analytics"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:analytics:read">
                                  <PlatformAnalyticsDashboardPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="monitoring"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:monitoring:read">
                                  <MonitoringDashboardPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="monitoring-settings"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:monitoring:read">
                                  <MonitoringSettingsPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="users-monitoring"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:monitoring:read">
                                  <UsersMonitoringPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="monitoring/:serverId"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:monitoring:read">
                                  <MonitoringDashboardPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="app-links-settings"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:settings:read">
                                  <AppLinksSettingsPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="modules"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:settings:read">
                                  <ModulesPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="regions"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:settings:read">
                                  <RegionSettingsPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="storage"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:settings:read">
                                  <StorageListPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="storage/regions/:regionCode"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:settings:read">
                                  <StorageRegionDetailPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route
                            path="storage/tenants/:tenantId"
                            element={
                              <SuperAdminGuard>
                                <PlatformPermissionGuard permission="platform:settings:read">
                                  <StorageTenantDetailPage />
                                </PlatformPermissionGuard>
                              </SuperAdminGuard>
                            }
                          />
                          <Route path="*" element={<NotFoundPage />} />
                          <Route
                            path="data-deletion"
                            element={
                              <RoleGuard roles={DATA_DELETION_ROLES}>
                                <PlatformPermissionGuard permission="platform:tenants:read">
                                  <DeletionsPage />
                                </PlatformPermissionGuard>
                              </RoleGuard>
                            }
                          />
                          <Route
                            path="data-export"
                            element={
                              <RoleGuard roles={DATA_EXPORT_ROLES}>
                                <PlatformPermissionGuard permission="platform:tenants:read">
                                  <ExportsPage />
                                </PlatformPermissionGuard>
                              </RoleGuard>
                            }
                          />
                        </Route>
                      </Routes>
                    </NavigationProvider>
                  </BrowserRouter>
                </BillingTransactionProvider>
              </ServiceProvider>
            </RoomCategoriesProvider>
          </RoomProvider>
          <Toaster />
          <ReactQueryDevtools initialIsOpen={false} />
        </NotificationsToastProvider>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
